import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Card, CardBody, CardHeader, Col, DropdownItem, DropdownMenu, DropdownToggle,
  Form, FormGroup, Input, Label, Row, UncontrolledDropdown,
} from "reactstrap";
import { Alert, Box, FormControlLabel, Switch, Typography } from "@mui/material";
import { toast } from "react-toastify";
import { Link, useHistory, useLocation } from "react-router-dom";

import AppModal from "../../../../Components/Common/AppModal";
import DeleteModal from "../../../../Components/Common/DeleteModal";
import LoadingSpinner from "../../../../Components/Common/Spinner";
import NoDataContent from "../../../../Components/Common/NoDataContent";
import TableContainer from "../../../../Components/Common/TableContainer";
import SettingsPanel from "../../components/SettingsPanel";
import PortalUrl from "../../../CustomerPortalConsole/PortalUrl";
import { MODE_LABELS, jobsModeOf, tasksModeOf } from "../../../CustomerPortalConsole/portalForm";

import { portalProfileApi, PP_ENDPOINTS, portalConsoleUrl } from "./api";
import { isSystemModuleActive, SYSTEM_MODULE_CODES } from "../../../../helpers/functions/systemModules";

/**
 * Customer Portal settings - the list of portals.
 *
 * Change #100781: a PortalProfile is now a whole portal with its own address,
 * branding and Jobs / Tasks sections. This screen lists them and creates new
 * ones (name only); everything else is set in the portal console
 * (/customer-portal-console/:portalId). The company-wide branded toggle stays here.
 */
// Change #100263 - the screen is both a Settings tab and the standalone
// /portal-profiles route. Standalone it needs page-content to clear the fixed
// top bar, or its first lines render underneath it; inside Settings that
// padding already exists and would be doubled.
const PageFrame = ({ children }) => {
  const { pathname } = useLocation();
  if (pathname.toLowerCase().startsWith("/portal-profiles")) {
    return <div className="page-content">{children}</div>;
  }
  return children;
};

const ModeBadge = ({ mode }) => {
  const cls = mode === "2" ? "bg-success-subtle text-success" : mode === "1" ? "bg-info-subtle text-info" : "bg-light text-muted";
  return <span className={`badge ${cls}`}>{MODE_LABELS[mode] || "Off"}</span>;
};

const PortalProfiles = () => {
  const history = useHistory();
  const [profiles, setProfiles] = useState([]);
  const [flags, setFlags] = useState({ portalUrls: true, portalLinks: true });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [branded, setBranded] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [list, brand] = await Promise.all([
      portalProfileApi(PP_ENDPOINTS.list, "GET"),
      portalProfileApi(PP_ENDPOINTS.brandingGet, "GET"),
    ]);
    setLoading(false);
    if (list?.status === 200) {
      setProfiles(list.portalprofile_list || []);
      // Older backend: no flags at all, so assume ready.
      setFlags({
        portalUrls: list.FlagPortalUrls === undefined ? true : !!list.FlagPortalUrls,
        portalLinks: list.FlagPortalLinks === undefined ? true : !!list.FlagPortalLinks,
      });
    } else {
      toast.error(list?.message || "Could not load the portals.");
    }
    if (brand?.status === 200) setBranded(!!brand.branded);
  }, []);

  const toggleBranded = async (value) => {
    setBranded(value);
    const res = await portalProfileApi(PP_ENDPOINTS.brandingSet, "POST", { branded: value });
    if (res?.status !== 200) {
      setBranded(!value);
      toast.error(res?.message || "Could not save the branding setting.");
    }
  };

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return profiles;
    return profiles.filter((p) =>
      [p.Name, p.Description, p.Url].filter(Boolean).some((v) => String(v).toLowerCase().includes(q))
    );
  }, [profiles, search]);

  const openCreate = () => {
    setNewName("");
    setCreateOpen(true);
  };

  const submitCreate = async (e) => {
    e?.preventDefault?.();
    const Name = newName.trim();
    if (!Name) {
      toast.error("Enter a portal name");
      return;
    }
    setSaving(true);
    const res = await portalProfileApi(PP_ENDPOINTS.create, "POST", { Name });
    setSaving(false);
    if (res?.status === 200 && res?.Id) {
      toast.success("Portal created");
      setCreateOpen(false);
      history.push(portalConsoleUrl(res.Id));
    } else {
      toast.error(res?.message || "Could not create the portal.");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    const res = await portalProfileApi(PP_ENDPOINTS.del(deleteTarget.Id), "DELETE");
    setSaving(false);
    if (res?.status === 200) {
      toast.success("Portal deleted");
      setDeleteTarget(null);
      load();
    } else {
      toast.error(res?.message || "Delete failed.");
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: "Name",
        accessor: "Name",
        filterable: false,
        Cell: (cell) => (
          <div>
            <Link to={portalConsoleUrl(cell.row.original.Id)} className="fw-medium">
              {cell.row.original.Name || "(no name)"}
            </Link>
            {cell.row.original.Description ? (
              <div className="text-muted fs-12 text-truncate" style={{ maxWidth: 260 }}>
                {cell.row.original.Description}
              </div>
            ) : null}
          </div>
        ),
      },
      {
        Header: "Portal address",
        accessor: "Url",
        filterable: false,
        Cell: (cell) => <PortalUrl url={cell.row.original.Url} portalUrlsReady={flags.portalUrls} compact />,
      },
      {
        Header: "Active",
        accessor: "FlagActive",
        filterable: false,
        Cell: (cell) =>
          String(cell.row.original.FlagActive) === "1" ? (
            <span className="badge bg-success-subtle text-success">Active</span>
          ) : (
            <span className="badge bg-light text-muted">Inactive</span>
          ),
      },
      { Header: "Priority", accessor: "Priority", filterable: false },
      {
        Header: "Contacts",
        accessor: "QtyContacts",
        filterable: false,
        Cell: (cell) => Number(cell.row.original.QtyContacts || 0),
      },
      {
        Header: "Jobs",
        accessor: "JobsMode",
        filterable: false,
        Cell: (cell) => <ModeBadge mode={jobsModeOf(cell.row.original)} />,
      },
      {
        Header: "Tasks",
        accessor: "TasksMode",
        filterable: false,
        Cell: (cell) => <ModeBadge mode={tasksModeOf(cell.row.original)} />,
      },
      {
        Header: "Action",
        filterable: false,
        Cell: (cell) => (
          <UncontrolledDropdown>
            <DropdownToggle className="btn btn-soft-info btn-icon fs-14" type="button">
              <i className="fa-solid fa-ellipsis" />
            </DropdownToggle>
            <DropdownMenu>
              <li>
                <DropdownItem onClick={() => history.push(portalConsoleUrl(cell.row.original.Id))}>
                  <i className="ri-settings-3-line align-bottom me-2 text-muted" />
                  Open console
                </DropdownItem>
              </li>
              <li>
                <DropdownItem onClick={() => setDeleteTarget(cell.row.original)}>
                  <i className="ri-delete-bin-fill align-bottom me-2 text-muted" />
                  Delete
                </DropdownItem>
              </li>
            </DropdownMenu>
          </UncontrolledDropdown>
        ),
      },
    ],
    [history, flags.portalUrls]
  );

  if (!isSystemModuleActive(SYSTEM_MODULE_CODES.CUSTOMER_JOBS_PORTAL)) {
    return (
      <PageFrame>
        <Box sx={{ p: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Customer Portal</Typography>
          <Alert severity="info">
            The <strong>Customer Jobs Portal</strong> module is turned off. Enable it in{" "}
            <Link to="/settings/5/1"><strong>Settings &rarr; Modules</strong></Link> to configure
            portals and branding.
          </Alert>
        </Box>
      </PageFrame>
    );
  }

  return (
    <PageFrame>
      <SettingsPanel
        title="Customer Portal"
        description="Each portal has its own address, branding and Jobs / Tasks sections. Give contacts access from the portal's Contacts tab or the customer's Portal Access tab."
      >
        {!flags.portalUrls && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Portal addresses appear once the database update for Change #100781 has run.
          </Alert>
        )}

        <Card className="mb-3">
          <CardBody>
            <FormControlLabel
              control={<Switch checked={branded} onChange={(e) => toggleBranded(e.target.checked)} />}
              label="Branded portal (show company / customer logo)"
            />
            <Typography variant="body2" color="text.secondary">
              When on, the portal shows the customer&apos;s own logo if one is set, then the portal&apos;s logo, then your company logo. Turn off for an unbranded portal.
            </Typography>
          </CardBody>
        </Card>

        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader className="border-0">
                <Row className="g-4 align-items-center">
                  <Col sm={4}>
                    <div className="search-box">
                      <Input
                        type="search"
                        className="form-control search"
                        placeholder="Search portals..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                      <i className="ri-search-line search-icon" />
                    </div>
                  </Col>
                  <div className="col-sm-auto ms-auto">
                    <button type="button" className="btn btn-success" onClick={openCreate}>
                      <i className="ri-add-line align-bottom me-1" />
                      Create Portal
                    </button>
                  </div>
                </Row>
              </CardHeader>
              <CardBody className="pt-0">
                {loading ? (
                  <LoadingSpinner />
                ) : filtered.length ? (
                  <TableContainer
                    columns={columns}
                    data={filtered}
                    isGlobalFilter={false}
                    isAddUserList={false}
                    customPageSize={10}
                    className="custom-header-css"
                    divClass="table-responsive table-card mb-0"
                    tableClass="align-middle table-nowrap"
                    theadClass="text-muted"
                  />
                ) : profiles.length ? (
                  <NoDataContent titleText="No portals found" paragraphText="Try another search keyword." />
                ) : (
                  <NoDataContent
                    titleText="No portals yet"
                    paragraphText="Create a portal, then choose what customers see and add contacts to it."
                    useButton={true}
                    buttonText="Create Portal"
                    buttonOnClick={openCreate}
                  />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </SettingsPanel>

      <DeleteModal
        show={!!deleteTarget}
        loading={saving}
        onCloseClick={() => setDeleteTarget(null)}
        onDeleteClick={confirmDelete}
        customText={
          deleteTarget
            ? `Delete the portal "${deleteTarget.Name}"? Its address stops working and its ${Number(deleteTarget.QtyContacts || 0)} contact(s) lose access through it.`
            : undefined
        }
      />

      <AppModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="New portal"
        headerAccent="indigo"
        size="sm"
        loading={saving}
        onSubmit={submitCreate}
        submitLabel="Create"
        submitDisabled={!newName.trim()}
      >
        <Form onSubmit={submitCreate}>
          <FormGroup className="mb-0">
            <Label for="portal-new-name">Portal name</Label>
            <Input
              id="portal-new-name"
              value={newName}
              autoFocus
              placeholder="e.g. Facilities clients"
              onChange={(e) => setNewName(e.target.value)}
              required
            />
            <div className="text-muted fs-12 mt-2">
              You set the branding, sections and contacts in the portal console next.
            </div>
          </FormGroup>
        </Form>
      </AppModal>
    </PageFrame>
  );
};

export default PortalProfiles;
